import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'success' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  glow?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  glow = false,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center font-semibold
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    relative overflow-hidden
  `;
  
  const variantClasses = {
    primary: `
      bg-gradient-to-r from-primary-500 to-primary-600
      hover:from-primary-400 hover:to-primary-500
      text-white font-semibold
      border border-primary-400/30
      shadow-lg shadow-primary-500/20
      hover:shadow-xl hover:shadow-primary-500/30
      hover:-translate-y-0.5
      focus:ring-primary-500/50
      active:translate-y-0
    `,
    secondary: `
      bg-gradient-to-r from-gray-700/80 to-gray-800/80
      hover:from-gray-600/90 hover:to-gray-700/90
      text-white font-semibold
      border border-gray-500/30
      shadow-lg shadow-gray-900/20
      hover:shadow-xl hover:shadow-gray-900/30
      hover:-translate-y-0.5
      focus:ring-gray-500/50
      backdrop-blur-sm
    `,
    outline: `
      bg-transparent
      hover:bg-white/5
      text-gray-200
      border border-gray-500/40
      hover:border-primary-400/50
      focus:ring-gray-500/50
      backdrop-blur-sm
    `,
    danger: `
      bg-gradient-to-r from-red-500 to-red-600
      hover:from-red-400 hover:to-red-500
      text-white font-semibold
      border border-red-400/30
      shadow-lg shadow-red-500/20
      hover:shadow-xl hover:shadow-red-500/30
      hover:-translate-y-0.5
      focus:ring-red-500/50
    `,
    success: `
      bg-gradient-to-r from-emerald-500 to-emerald-600
      hover:from-emerald-400 hover:to-emerald-500
      text-white font-semibold
      border border-emerald-400/30
      shadow-lg shadow-emerald-500/20
      hover:shadow-xl hover:shadow-emerald-500/30
      hover:-translate-y-0.5
      focus:ring-emerald-500/50
    `,
    ghost: `
      bg-transparent
      hover:bg-white/10
      text-gray-300
      hover:text-white
      focus:ring-white/20
    `,
  };
  
  const sizeClasses = {
    sm: 'text-xs px-3 py-2 rounded-lg gap-1.5',
    md: 'text-sm px-5 py-2.5 rounded-xl gap-2',
    lg: 'text-base px-7 py-3.5 rounded-xl gap-2.5',
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  const glowClass = glow && !disabled ? 'pulse-glow' : '';

  return (
    <button
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${widthClass}
        ${glowClass}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      disabled={disabled || isLoading}
      {...props}
    >
      {/* Shine effect overlay */}
      {!disabled && variant !== 'ghost' && variant !== 'outline' && (
        <span 
          className="absolute inset-0 overflow-hidden rounded-inherit pointer-events-none"
          style={{ borderRadius: 'inherit' }}
        >
          <span 
            className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500"
            style={{
              background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.1) 45%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 55%, transparent 60%)',
              transform: 'translateX(-100%)',
            }}
          />
        </span>
      )}
      
      {isLoading && (
        <svg 
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      
      {!isLoading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      <span className="relative">{children}</span>
      {!isLoading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </button>
  );
};
